import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {BehaviorSubject, merge, Observable, of} from 'rxjs';
import {catchError, filter, finalize, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../../destroyable.component';
import {LocalizationService} from '../../../../shared/localization/localization.service';
import {isMobileDevice} from '../../../../shared/utils/browser';
import {Task, TasksResponse} from '../../../../shared/workflow/workflow-v2.interface';
import {tasks_per_page, WorkflowV2Service} from '../../../../shared/workflow/workflow-v2.service';
import {CommunicationsService} from '../../../shared/communications.service';

@Component({
  selector: 'gp-tasks-in-progress',
  templateUrl: './tasks-in-progress.component.html',
  styleUrls: [
    '../../workflows-common.component.scss',
    '../../workflows.component.scss',
    'tasks-in-progress.component.scss'
  ]
})
export class TasksInProgressComponent extends Destroyable implements OnInit {
  public size = tasks_per_page;

  public tasks: Task[] = [];
  public currentPage = 0;
  public total: number;
  public trigger$: Observable<number>;
  public loading = true;
  public isMobileDevice = isMobileDevice;

  @ViewChild('tasksList')
  private tasksListElementRef: ElementRef;
  private loadMore$ = new BehaviorSubject<number>(null);

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private localization: LocalizationService,
              private workflowService: WorkflowV2Service,
              private communicationsServices: CommunicationsService) {
    super();
    this.trigger$ = merge(
      this.localization.changeDateFormatEvent,
      this.localization.changeLanguageEvent
    ).pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );

    this.activatedRoute.queryParams.subscribe((queryParams) => this.currentPage = queryParams.page || 0);
  }

  public ngOnInit(): void {
    this.scrollToTopWhenClickOnRecentTask();
    this.communicationsServices.notificationUpdated
      .pipe(
        switchMap(() => this.getTasks$(0, true)),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
    this.loadMore$.pipe(
      filter(Boolean),
      switchMap((page: number) => {
        this.loading = true;
        return this.getTasks$(page);
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private getTasks$(page: number, reset = false) {
    return this.workflowService.getTasks({'per-page': this.size, page})
      .pipe(
        catchError(() => of({data: [], total: 0})),
        tap((response: TasksResponse) => {
          this.total = response.total;
          this.tasks = reset ? response.data : [...this.tasks, ...response.data];
        }),
        finalize(() => this.loading = false)
      );
  }

  private scrollToTopWhenClickOnRecentTask(): void {
    this.communicationsServices.recentListClicked
      .pipe(
        tap(() => {
          if (this.tasksListElementRef) {
            this.tasksListElementRef.nativeElement.scrollTop = 0;
          }
        }),
        takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public trackByTaskId(index: number, task: Task): string {
    return task.id;
  }

  public loadMore(): void {
    if (!this.loading) {
      this.currentPage++;
      this.loadMore$.next(this.currentPage);
    }
  }

  public isCanceled(task: Task) {
    return task.translationKeys.subtitle.includes('_canceled');
  }

  public isDeleted(task: Task) {
    return task.translationKeys.subtitle.includes('_deleted');
  }

  public viewTask(task: Task) {
    if (task.status === 'IN_PROGRESS') {
      this.router.navigate(['/inbox/tasks', task.id]);
    }
  }
}
